/**
 * Lightweight A/B experiment framework.
 *
 * - Sticky assignment per browser via localStorage.
 * - Deterministic 50/50 (or N-way) split from crypto random on first exposure.
 * - Exposure events piped through observability so analysis can join
 *   variant → conversion via funnel_events metadata.
 *
 * Usage:
 *   const variant = getVariant("hero_cta_v1", ["A", "B"] as const);
 *   // renders copy for A or B
 *
 * Analysis:
 *   Every cta_click / access_request_submitted event now carries
 *   `experiments: { hero_cta_v1: "A" | "B" }` in metadata.
 */

const STORAGE_PREFIX = "myelina.exp.";
const exposureLogged = new Set<string>();

const readAssignment = (key: string): string | null => {
  try {
    return localStorage.getItem(STORAGE_PREFIX + key);
  } catch {
    return null;
  }
};

const writeAssignment = (key: string, value: string) => {
  try {
    localStorage.setItem(STORAGE_PREFIX + key, value);
  } catch {
    /* ignore */
  }
};

/**
 * Get (or assign) a sticky variant for the given experiment.
 * Safe on SSR — returns the first variant deterministically.
 */
export function getVariant<T extends readonly string[]>(
  key: string,
  variants: T,
): T[number] {
  if (typeof window === "undefined") return variants[0];
  const existing = readAssignment(key);
  if (existing && variants.includes(existing as T[number])) {
    return existing as T[number];
  }
  const idx = Math.floor(Math.random() * variants.length);
  const picked = variants[idx];
  writeAssignment(key, picked);
  return picked;
}

/**
 * Fire a one-time exposure event per experiment/variant per page load.
 * Records into funnel_events metadata via observability.trackEvent so
 * we can reconstruct sample size in analysis.
 */
export function trackExposure(key: string, variant: string) {
  if (typeof window === "undefined") return;
  const tag = `${key}:${variant}`;
  if (exposureLogged.has(tag)) return;
  exposureLogged.add(tag);
  // Fire local + funnel event; both are fire-and-forget.
  import("@/lib/observability").then(({ trackEvent }) =>
    trackEvent("experiment_exposure", { experiment: key, variant }),
  );
  import("@/lib/funnelAnalytics").then(({ trackFunnelEvent }) =>
    // reuse landing_view metadata channel — safe enum-free string
    (trackFunnelEvent as unknown as (
      n: string,
      m: Record<string, unknown>,
    ) => void)("landing_view", {
      experiment_exposure: true,
      experiment: key,
      variant,
    }),
  );
}

/** Return the map of all sticky assignments for downstream event metadata. */
export function getAllAssignments(): Record<string, string> {
  const out: Record<string, string> = {};
  if (typeof window === "undefined") return out;
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k || !k.startsWith(STORAGE_PREFIX)) continue;
      const v = localStorage.getItem(k);
      if (v) out[k.slice(STORAGE_PREFIX.length)] = v;
    }
  } catch {
    /* ignore */
  }
  return out;
}

/** Registered experiment keys — keep in one place to avoid typos. */
export const EXPERIMENTS = {
  heroCtaV1: "hero_cta_v1",
  heroPrimaryCtaV1: "hero_primary_cta_v1",
  heroSecondaryCtaV1: "hero_secondary_cta_v1",
  signupPrimaryV1: "signup_primary_cta_v1",
  signupPhoneV1: "signup_phone_cta_v1",
  whoWeArePrimaryV1: "who_we_are_primary_cta_v1",
  cliniciansInquiryPrimaryV1: "clinicians_inquiry_primary_cta_v1",
} as const;

/**
 * Registry of copy variants for CTA A/B tests.
 * Each entry maps a sticky variant id to the exact label(s) rendered.
 * Keep every label PCM+NLP compliant, invite-only, and free of blame language.
 */
export const CTA_VARIANT_COPY = {
  [EXPERIMENTS.heroPrimaryCtaV1]: {
    A: "Request access",
    B: "Request your invitation",
    C: "Ask for an invite",
  },
  [EXPERIMENTS.heroSecondaryCtaV1]: {
    A: "See the demo",
    B: "See a day with Myelina",
    C: "Walk through a day",
  },
  [EXPERIMENTS.signupPrimaryV1]: {
    A: { idle: "Create account", loading: "Creating account…" },
    B: { idle: "Step inside Myelina", loading: "Opening…" },
    C: { idle: "Begin quietly", loading: "Beginning…" },
  },
  [EXPERIMENTS.signupPhoneV1]: {
    A: { send: "Send code", verify: "Verify & create account", loading: "Verifying…" },
    B: { send: "Text me a code", verify: "Step inside Myelina", loading: "Opening…" },
  },
  [EXPERIMENTS.whoWeArePrimaryV1]: {
    A: "Request access",
    B: "Sit with us — request access",
    C: "Meet Myelina — request access",
  },
  [EXPERIMENTS.cliniciansInquiryPrimaryV1]: {
    A: "Send inquiry",
    B: "Start the conversation",
    C: "Introduce your practice",
  },
} as const;

